import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert
} from '@mui/material';
import {
  Save as SaveIcon,
  DeleteOutline as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useFormContext } from '../context/FormContext';

interface DraftIndicatorProps {
  currentStep: number;
  onClearDraft?: () => void;
}

const DraftIndicator: React.FC<DraftIndicatorProps> = ({ currentStep, onClearDraft }) => {
  const { draftStatus, saveDraft, deleteDraft, resetForm } = useFormContext();
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isSavingManually, setIsSavingManually] = useState(false);

  // Format last saved time
  const getLastSavedText = () => {
    if (!draftStatus.lastSaved) return '';

    const now = new Date();
    const diff = now.getTime() - draftStatus.lastSaved.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Saved just now';
    if (minutes === 1) return 'Saved 1 minute ago';
    if (minutes < 60) return `Saved ${minutes} minutes ago`;

    const hours = Math.floor(minutes / 60);
    if (hours === 1) return 'Saved 1 hour ago';
    return `Saved ${hours} hours ago`;
  };

  const handleManualSave = async () => {
    setIsSavingManually(true);
    try {
      await saveDraft(currentStep);
    } finally {
      setIsSavingManually(false);
    }
  };

  const handleClearDraft = async () => {
    setIsClearing(true);
    try {
      const result = await deleteDraft();
      if (result.success) {
        resetForm();
        setShowClearDialog(false);
        if (onClearDraft) {
          onClearDraft();
        }
      } else {
        console.error('Failed to clear draft:', result.error);
      }
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {draftStatus.isSaving || isSavingManually ? (
            <>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Saving progress...
              </Typography>
            </>
          ) : draftStatus.error ? (
            <>
              <ErrorIcon sx={{ color: 'warning.main' }} fontSize="small" />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Could not save progress - {draftStatus.error}
              </Typography>
            </>
          ) : draftStatus.lastSaved ? (
            <>
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                {getLastSavedText()}
              </Typography>
            </>
          ) : null}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleManualSave}
            disabled={draftStatus.isSaving || isSavingManually}
          >
            Save Progress
          </Button>

          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setShowClearDialog(true)}
          >
            Clear & Start Over
          </Button>
        </Box>
      </Box>

      <Dialog
        open={showClearDialog}
        onClose={() => !isClearing && setShowClearDialog(false)}
      >
        <DialogTitle>Clear Worksheet?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear this worksheet and start over? This action cannot be undone.
          </DialogContentText>
          {draftStatus.lastSaved && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Your saved progress from {getLastSavedText().toLowerCase()} will be deleted.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowClearDialog(false)}
            disabled={isClearing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearDraft}
            color="error"
            variant="contained"
            disabled={isClearing}
            startIcon={isClearing ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {isClearing ? 'Clearing...' : 'Clear Worksheet'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DraftIndicator;
